import { useState } from 'react';
import { Box, Flex, Spinner } from '@chakra-ui/react';
import { Text } from '@kula-ai/components';
import { ChevronRight, Plus } from '@kula-ai/icons-core';
import { useChatSubmit } from '../../../hooks';
import { useAgentForgeStore } from '../../../stores';
import type { ClarifyBlockProps } from './ClarifyBlock.types';

// A "create new …" option is an action, not a real choice — detected by label
// so it can be styled apart (dashed + plus). Harmless when nothing matches.
const isCreateOption = (label: string): boolean =>
  /^create\b/i.test(label.trim());

// A cancel-ish option means "do nothing" — that must not cost an LLM turn.
// Sending it produced a garbled bubble ("Add the 2 selected candidates to
// cancel (Cancel).") plus a full agent round-trip whose only output was
// "Cancelled". The click now dismisses the card locally; the agent's
// workflow already ended when the form was emitted, so nothing is waiting
// on an answer server-side.
const isCancelOption = (value: string, label: string): boolean =>
  /^(cancel|dismiss|never ?mind|no,? (thanks|cancel))$/i.test(value.trim()) ||
  /^(cancel|dismiss|never ?mind)$/i.test(label.trim());

// Renders a single-choice picker emitted by the backend's propose_clarify
// tool. Clicking an option submits the chip's *pending* template with the
// chosen value substituted in — so the next agent turn already has the
// missing ID. Same pattern the candidate suggested-action chips use.
export const ClarifyBlock = ({ data }: ClarifyBlockProps) => {
  const { send, isSending } = useChatSubmit();
  // Don't allow re-picking while a turn is already in flight.
  const isStreaming = useAgentForgeStore((s) => {
    const sid = s.activeSessionId;
    if (!sid) return false;
    const tab = s.openTabs.find((t) => t.sessionId === sid);
    return tab?.status === 'streaming' || s.streamingDraft?.sessionId === sid;
  });
  const [dismissed, setDismissed] = useState(false);
  // The picked option keeps a filled/pressed look (and a spinner while the
  // turn runs) — before this, a click left every option identical and the
  // card never confirmed which one was chosen.
  const [pickedValue, setPickedValue] = useState<string | null>(null);
  const disabled = isSending || isStreaming;

  // Materialize the message the click sends: substitute the user-picked
  // value into the backend-supplied pending template. The label is also
  // appended in parentheses so the chat history reads naturally
  // ("Add candidates ... to job 489 (Senior Ruby Engineer)") rather than
  // a bare numeric ID.
  const sendChoice = (value: string, label: string) => {
    if (disabled) return;
    if (isCancelOption(value, label)) {
      setDismissed(true);
      return;
    }
    const filled = data.pending.includes('{value}')
      ? data.pending.replace('{value}', `${value} (${label})`)
      : `${data.pending} ${value} (${label})`;
    setPickedValue(value);
    void send(filled);
  };

  if (dismissed) {
    return (
      <Box
        data-test-id="agentforge-clarify-block-dismissed"
        border="1px solid"
        borderColor="secondary-200"
        borderRadius="10px"
        px="14px"
        py="10px"
        bg="secondary-25"
      >
        <Text variant="small" className="text-secondary-500">
          Dismissed — nothing was changed.
        </Text>
      </Box>
    );
  }

  return (
    <Box
      data-test-id="agentforge-clarify-block"
      bg="base-white"
      border="1px solid"
      borderColor="primary-200"
      borderRadius="10px"
      overflow="hidden"
    >
      {/* Question */}
      <Box
        px="14px"
        py="10px"
        borderBottom="1px solid"
        borderColor="primary-100"
        bg="primary-25"
      >
        <Text variant="label" as="span" className="text-primary-900">
          {data.intro}
        </Text>
      </Box>

      {/* Option buttons. Render as vertical stack so labels can wrap and
          IDs stay legible — chips would truncate long job titles. Each row
          carries a trailing chevron to read as selectable; a "create new …"
          option is set apart with a dashed border + plus glyph. */}
      <Flex direction="column" gap="4px" px="14px" py="10px">
        {data.options.map((o) => {
          const isCreate = isCreateOption(o.label);
          const isPicked = pickedValue === o.value;
          return (
            <Flex
              key={o.value}
              as="button"
              type="button"
              role="group"
              aria-pressed={isPicked}
              disabled={disabled}
              opacity={disabled && !isPicked ? 0.5 : 1}
              cursor={disabled ? 'not-allowed' : 'pointer'}
              onClick={() => sendChoice(o.value, o.label)}
              align="center"
              justify="space-between"
              gap="10px"
              px="12px"
              py="9px"
              mt={isCreate ? '4px' : undefined}
              borderRadius="8px"
              border="1px solid"
              borderStyle={isCreate ? 'dashed' : 'solid'}
              borderColor={
                isPicked
                  ? 'primary-400'
                  : isCreate
                    ? 'primary-300'
                    : 'secondary-200'
              }
              bg={isPicked ? 'primary-25' : 'base-white'}
              textAlign="left"
              transition="background-color 0.12s ease, border-color 0.12s ease"
              _hover={
                !disabled
                  ? {
                      bg: isCreate ? 'primary-50' : 'primary-25',
                      borderColor: 'primary-300',
                      '& .clarify-chevron': { color: 'primary-500' },
                    }
                  : undefined
              }
              data-test-id={`agentforge-clarify-option-${o.value}`}
            >
              <Flex align="center" gap="8px" minW="0">
                {isCreate && (
                  <Box
                    as="span"
                    color="primary-600"
                    display="inline-flex"
                    flexShrink={0}
                  >
                    <Plus size={16} />
                  </Box>
                )}
                <Text
                  variant={isCreate ? 'label' : 'p'}
                  as="span"
                  className={
                    isCreate ? 'text-primary-700' : 'text-secondary-900'
                  }
                >
                  {o.label}
                </Text>
              </Flex>
              {isPicked && disabled ? (
                <Spinner size="xs" color="primary.500" flexShrink={0} />
              ) : (
                !isCreate && (
                  <Box
                    as="span"
                    className="clarify-chevron"
                    color="secondary-400"
                    display="inline-flex"
                    flexShrink={0}
                  >
                    <ChevronRight size={16} />
                  </Box>
                )
              )}
            </Flex>
          );
        })}
      </Flex>

      {data.submitLabel && (
        <Box
          px="14px"
          py="8px"
          borderTop="1px solid"
          borderColor="secondary-100"
          bg="secondary-25"
        >
          <Text variant="small" className="text-secondary-600">
            {data.submitLabel}
          </Text>
        </Box>
      )}
    </Box>
  );
};
